import React, { useState } from "react";
import { ACCESS_ROLES, localExpiry, quotaValue } from "../../utils/accessManagement";

export default function UserAccessForm({ user, administrators, superadmin, ownUserId, busy, onSave }) {
    const creating = !user;
    const superuser = Boolean(user?.is_superuser);
    const [draft, setDraft] = useState(() => ({
        email: user?.email || "", password: "",
        role: superuser ? "superadmin" : user?.role || "viewer",
        administrator: user?.administrator ?? (superadmin ? "" : ownUserId),
        expiresAt: localExpiry(user?.expires_at),
        max_tags: user?.max_tags ?? "", max_servers: user?.max_servers ?? "", max_users: user?.max_users ?? "",
    }));
    const [error, setError] = useState("");
    const rootRole = ["admin", "superadmin"].includes(draft.role);
    const set = (key) => (event) => setDraft((current) => ({ ...current, [key]: event.target.value }));
    const submit = async (event) => {
        event.preventDefault();
        setError("");
        try {
            const originalExpiry = user?.expires_at;
            const expiresAt = draft.expiresAt === localExpiry(originalExpiry)
                ? originalExpiry || null
                : draft.expiresAt ? new Date(draft.expiresAt).toISOString() : null;
            const data = {
                role: draft.role,
                administrator: rootRole ? null : Number(draft.administrator),
                expires_at: expiresAt,
                max_tags: quotaValue(draft.max_tags),
                max_servers: draft.role === "admin" ? quotaValue(draft.max_servers) : null,
                max_users: draft.role === "admin" ? quotaValue(draft.max_users) : null,
            };
            if (!rootRole && !data.administrator) throw new Error("Select an administrator.");
            if (creating) Object.assign(data, { email: draft.email, password: draft.password });
            await onSave(data);
            // Never retain a new account's password after a successful save.
            setDraft((current) => ({ ...current, password: "" }));
        } catch (failure) {
            setError(failure.message || "Unable to save the account.");
        }
    };
    return (
        <section className="access-panel" aria-labelledby="access-account-heading">
            <h2 id="access-account-heading">{creating ? "Create user" : user.role ? "Account policy" : "Assign account policy"}</h2>
            <form onSubmit={submit}>
                <fieldset disabled={busy}>
                    <div className="access-grid">
                        <label>Email<input type="email" autoComplete="off" required readOnly={!creating} value={draft.email} onChange={set("email")} /></label>
                        {creating && <label>Initial password<input type="password" autoComplete="new-password" required value={draft.password} onChange={set("password")} /></label>}
                        <label>Role<select value={draft.role} onChange={set("role")} disabled={superuser}>
                            {(superuser ? ["superadmin"] : ACCESS_ROLES.filter((role) => superadmin || role !== "admin")).map((role) => <option key={role} value={role}>{role}</option>)}
                        </select></label>
                        {!rootRole && superadmin && <label>Administrator<select required value={draft.administrator} onChange={set("administrator")}>
                            <option value="">Select administrator</option>
                            {administrators.map((admin) => <option key={admin.id} value={admin.id}>{admin.email}</option>)}
                        </select></label>}
                        <label>Expiry (your local time)<input type="datetime-local" step="1" value={draft.expiresAt} onChange={set("expiresAt")} /></label>
                        {!superuser && <label>{draft.role === "admin" ? "Tag limit for this administrator and all users" : "Tag limit"}<input type="number" min="0" max="2147483647" step="1" value={draft.max_tags} onChange={set("max_tags")} /></label>}
                        {draft.role === "admin" && <>
                            <label>Shared server limit<input type="number" min="0" max="2147483647" step="1" value={draft.max_servers} onChange={set("max_servers")} /></label>
                            <label>Subordinate user limit<input type="number" min="0" max="2147483647" step="1" value={draft.max_users} onChange={set("max_users")} /></label>
                        </>}
                    </div>
                    <p className="access-hint">Blank expiry means no expiry. Blank limit means unlimited; 0 means no capacity. An administrator's limits and expiry still apply to their users.</p>
                    {error && <p className="access-error" role="alert">{error}</p>}
                    <button type="submit">{busy ? "Saving…" : creating ? "Create user" : "Save account policy"}</button>
                </fieldset>
            </form>
        </section>
    );
}
